import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { 
  formatCurrency, formatPercentage, formatDate,
  exportToPDF, exportToExcel, getWeekNumber
} from '../utils/helpers';
import {
  Plus, CalendarIcon, Wallet, TrendingUp, TrendingDown,
  FileText, FileSpreadsheet, Filter, PiggyBank, ShoppingCart, Package, PartyPopper
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, LineChart, Line, Legend
} from 'recharts';

const KostenMargesPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [costs, setCosts] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [fixedCosts, setFixedCosts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fixedCostDialog, setFixedCostDialog] = useState(false);
  
  // Inkoop data from Voorraad & Inkoop module
  const [invoices, setInvoices] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  
  // Inkoop trend data
  const [inkoopTrend, setInkoopTrend] = useState(null);
  
  // Event-related costs
  const [eventCosts, setEventCosts] = useState([]);
  
  // Arrangement-related costs
  const [arrangementCosts, setArrangementCosts] = useState([]);
  
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
    location: 'all'
  });
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    week_number: getWeekNumber(new Date()),
    food_cost: '',
    beverage_cost: '',
    personnel_cost: '',
    other_costs: '',
    location_id: ''
  });

  const [fixedCostData, setFixedCostData] = useState({
    name: '',
    amount: '',
    frequency: 'maand'
  });

  useEffect(() => {
    fetchData();
  }, [filters.startDate, filters.endDate, filters.location]);

  const fetchData = async () => {
    try {
      const params = {
        start_date: filters.startDate.toISOString().split('T')[0],
        end_date: filters.endDate.toISOString().split('T')[0]
      };
      if (filters.location !== 'all') params.location_id = filters.location;

      const [costsRes, revenueRes, fixedRes, locationsRes, invoicesRes, ordersRes, trendRes, eventCostsRes, arrCostsRes] = await Promise.all([
        api.get('/costs', { params }),
        api.get('/revenue', { params }),
        api.get('/fixed-costs'),
        api.get('/locations'),
        api.get('/invoices').catch(() => ({ data: [] })),
        api.get('/purchase-orders').catch(() => ({ data: [] })),
        api.get('/costs/inkoop-trend?weeks=8').catch(() => ({ data: null })),
        api.get('/event-costs').catch(() => ({ data: [] })),
        api.get('/arrangement-costs').catch(() => ({ data: [] }))
      ]);
      
      setCosts(costsRes.data);
      setRevenue(revenueRes.data);
      setFixedCosts(fixedRes.data);
      setLocations(locationsRes.data);
      setInkoopTrend(trendRes.data);
      
      // Filter event costs by date range
      const filteredEventCosts = (eventCostsRes.data || []).filter(ec => {
        const costDate = new Date(ec.date || ec.created_at);
        return costDate >= filters.startDate && costDate <= filters.endDate;
      });
      setEventCosts(filteredEventCosts);
      
      // Filter arrangement costs by date range
      const filteredArrCosts = (arrCostsRes.data || []).filter(ac => {
        const costDate = new Date(ac.date || ac.created_at);
        return costDate >= filters.startDate && costDate <= filters.endDate;
      });
      setArrangementCosts(filteredArrCosts);
      
      // Filter invoices by date range
      const filteredInvoices = (invoicesRes.data || []).filter(inv => {
        const invDate = new Date(inv.invoice_date || inv.created_at);
        return invDate >= filters.startDate && invDate <= filters.endDate;
      });
      setInvoices(filteredInvoices);
      
      // Filter purchase orders by date range (only delivered/received)
      const filteredOrders = (ordersRes.data || []).filter(order => {
        if (order.status !== 'geleverd' && order.status !== 'ontvangen') return false;
        const orderDate = new Date(order.order_date || order.created_at);
        return orderDate >= filters.startDate && orderDate <= filters.endDate;
      });
      setPurchaseOrders(filteredOrders);
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/costs', {
        ...formData,
        food_cost: parseFloat(formData.food_cost) || 0,
        beverage_cost: parseFloat(formData.beverage_cost) || 0,
        personnel_cost: parseFloat(formData.personnel_cost) || 0,
        other_costs: parseFloat(formData.other_costs) || 0,
        location_id: formData.location_id || null
      });
      toast.success('Kosten toegevoegd');
      setDialogOpen(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        week_number: getWeekNumber(new Date()),
        food_cost: '',
        beverage_cost: '',
        personnel_cost: '',
        other_costs: '',
        location_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddFixedCost = async (e) => {
    e.preventDefault();
    try {
      await api.post('/fixed-costs', null, {
        params: {
          name: fixedCostData.name,
          amount: parseFloat(fixedCostData.amount) || 0,
          frequency: fixedCostData.frequency
        }
      });
      toast.success('Vaste kosten toegevoegd');
      setFixedCostDialog(false);
      setFixedCostData({ name: '', amount: '', frequency: 'maand' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  // Calculate totals
  const totalRevenue = revenue.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalFoodCost = costs.reduce((sum, e) => sum + (e.food_cost || 0), 0);
  const totalBeverageCost = costs.reduce((sum, e) => sum + (e.beverage_cost || 0), 0);
  const totalPersonnelCost = costs.reduce((sum, e) => sum + (e.personnel_cost || 0), 0);
  const totalOtherCosts = costs.reduce((sum, e) => sum + (e.other_costs || 0), 0);
  const totalFixedCosts = fixedCosts.reduce((sum, e) => sum + (e.amount || 0), 0);
  
  // Inkoop kosten (from Voorraad & Inkoop module)
  const totalInvoiceCosts = invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const totalOrderCosts = purchaseOrders.reduce((sum, order) => sum + (order.total || order.total_amount || 0), 0);
  const totalInkoopKosten = totalInvoiceCosts + totalOrderCosts;
  
  // Event kosten (from Marketing & Acties module)
  const totalEventCosts = eventCosts.reduce((sum, ec) => sum + (ec.amount || 0), 0);
  
  // Group event costs by event
  const eventCostsByEvent = eventCosts.reduce((acc, ec) => {
    const eventName = ec.event_name || 'Onbekend Event';
    if (!acc[eventName]) acc[eventName] = { amount: 0, items: [] };
    acc[eventName].amount += ec.amount || 0;
    acc[eventName].items.push(ec);
    return acc;
  }, {});
  
  // Arrangement kosten (from Marketing & Acties module)
  const totalArrangementCosts = arrangementCosts.reduce((sum, ac) => sum + (ac.amount || 0), 0);
  
  // Group arrangement costs by arrangement
  const arrangementCostsByArr = arrangementCosts.reduce((acc, ac) => {
    const arrName = ac.arrangement_name || 'Onbekend Arrangement';
    if (!acc[arrName]) acc[arrName] = { amount: 0, items: [] };
    acc[arrName].amount += ac.amount || 0;
    acc[arrName].items.push(ac);
    return acc;
  }, {});
  
  // Group invoice costs by supplier
  const invoicesBySupplier = invoices.reduce((acc, inv) => {
    const supplier = inv.supplier_name || 'Onbekend';
    if (!acc[supplier]) acc[supplier] = 0;
    acc[supplier] += inv.total_amount || 0;
    return acc;
  }, {});
  
  // Group order costs by supplier
  const ordersBySupplier = purchaseOrders.reduce((acc, order) => {
    const supplier = order.supplier_name || 'Onbekend';
    if (!acc[supplier]) acc[supplier] = 0;
    acc[supplier] += order.total || order.total_amount || 0;
    return acc;
  }, {});
  
  // Combine supplier costs
  const allSupplierCosts = { ...invoicesBySupplier };
  Object.entries(ordersBySupplier).forEach(([supplier, amount]) => {
    allSupplierCosts[supplier] = (allSupplierCosts[supplier] || 0) + amount;
  });
  
  const totalCosts = totalFoodCost + totalBeverageCost + totalPersonnelCost + totalOtherCosts;
  const totalAllCosts = totalCosts + totalInkoopKosten + totalEventCosts + totalArrangementCosts; // Include inkoop, event and arrangement costs in total
  const bruttoWinst = totalRevenue - totalFoodCost - totalBeverageCost - totalInkoopKosten - totalEventCosts - totalArrangementCosts;
  const nettoResultaat = totalRevenue - totalAllCosts - totalFixedCosts;
  
  const foodCostPercentage = totalRevenue > 0 ? (totalFoodCost / totalRevenue) * 100 : 0;
  const beverageCostPercentage = totalRevenue > 0 ? (totalBeverageCost / totalRevenue) * 100 : 0;
  const personnelCostPercentage = totalRevenue > 0 ? (totalPersonnelCost / totalRevenue) * 100 : 0;
  const inkoopCostPercentage = totalRevenue > 0 ? (totalInkoopKosten / totalRevenue) * 100 : 0;
  const eventCostPercentage = totalRevenue > 0 ? (totalEventCosts / totalRevenue) * 100 : 0;
  const arrangementCostPercentage = totalRevenue > 0 ? (totalArrangementCosts / totalRevenue) * 100 : 0;
  const bruttoMargePercentage = totalRevenue > 0 ? (bruttoWinst / totalRevenue) * 100 : 0;

  // Chart data by week
  const chartData = costs
    .reduce((acc, entry) => {
      const weekKey = `Week ${entry.week_number}`;
      const existing = acc.find(a => a.week === weekKey);
      const revenueForWeek = revenue
        .filter(r => getWeekNumber(new Date(r.date)) === entry.week_number)
        .reduce((sum, r) => sum + (r.amount || 0), 0);
      
      if (existing) {
        existing.food_cost += entry.food_cost || 0;
        existing.beverage_cost += entry.beverage_cost || 0;
        existing.personnel_cost += entry.personnel_cost || 0;
        existing.revenue = Math.max(existing.revenue, revenueForWeek);
      } else {
        acc.push({
          week: weekKey,
          food_cost: entry.food_cost || 0,
          beverage_cost: entry.beverage_cost || 0,
          personnel_cost: entry.personnel_cost || 0,
          revenue: revenueForWeek
        });
      }
      return acc;
    }, [])
    .sort((a, b) => parseInt(a.week.split(' ')[1]) - parseInt(b.week.split(' ')[1]));

  const handleExportPDF = () => {
    const columns = [
      { header: 'Datum', key: 'date' },
      { header: 'Week', key: 'week_number' },
      { header: 'Food Cost', key: 'foodFormatted' },
      { header: 'Beverage Cost', key: 'beverageFormatted' },
      { header: 'Personeel', key: 'personnelFormatted' },
      { header: 'Overig', key: 'otherFormatted' }
    ];
    const data = costs.map(c => ({
      ...c,
      date: formatDate(c.date),
      foodFormatted: formatCurrency(c.food_cost),
      beverageFormatted: formatCurrency(c.beverage_cost),
      personnelFormatted: formatCurrency(c.personnel_cost),
      otherFormatted: formatCurrency(c.other_costs)
    }));
    exportToPDF('Kosten & Marges', columns, data, 'kosten-marges');
  };

  const handleExportExcel = () => {
    const columns = [
      { header: 'Datum', key: 'date' },
      { header: 'Week', key: 'week_number' },
      { header: 'Food Cost', key: 'food_cost' },
      { header: 'Beverage Cost', key: 'beverage_cost' },
      { header: 'Personeel', key: 'personnel_cost' },
      { header: 'Overig', key: 'other_costs' }
    ];
    exportToExcel('Kosten & Marges', columns, costs, 'kosten-marges');
  };

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="kosten-marges-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Kosten & Marges
          </h1>
          <p className="text-[#57534E] mt-1">
            Analyseer uw kosten en bereken marges
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={fixedCostDialog} onOpenChange={setFixedCostDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-secondary">
                <PiggyBank className="w-4 h-4 mr-2" />
                Vaste kosten
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Vaste kosten toevoegen</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddFixedCost} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Omschrijving</Label>
                  <Input
                    value={fixedCostData.name}
                    onChange={(e) => setFixedCostData({ ...fixedCostData, name: e.target.value })}
                    placeholder="bijv. Huur, Verzekering"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bedrag (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={fixedCostData.amount}
                    onChange={(e) => setFixedCostData({ ...fixedCostData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Frequentie</Label>
                  <Select
                    value={fixedCostData.frequency}
                    onValueChange={(value) => setFixedCostData({ ...fixedCostData, frequency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maand">Per maand</SelectItem>
                      <SelectItem value="kwartaal">Per kwartaal</SelectItem>
                      <SelectItem value="jaar">Per jaar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full btn-primary">
                  Toevoegen
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-cost-btn">
                <Plus className="w-4 h-4 mr-2" />
                Kosten invoeren
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Kosten invoeren</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Datum</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        date: e.target.value,
                        week_number: getWeekNumber(new Date(e.target.value))
                      })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Week nummer</Label>
                    <Input
                      type="number"
                      value={formData.week_number}
                      onChange={(e) => setFormData({ ...formData, week_number: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Food Cost (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.food_cost}
                      onChange={(e) => setFormData({ ...formData, food_cost: e.target.value })}
                      placeholder="0.00"
                      data-testid="cost-food-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Beverage Cost (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.beverage_cost}
                      onChange={(e) => setFormData({ ...formData, beverage_cost: e.target.value })}
                      placeholder="0.00"
                      data-testid="cost-beverage-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Personeelskosten (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.personnel_cost}
                      onChange={(e) => setFormData({ ...formData, personnel_cost: e.target.value })}
                      placeholder="0.00"
                      data-testid="cost-personnel-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Overige kosten (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.other_costs}
                      onChange={(e) => setFormData({ ...formData, other_costs: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Locatie (optioneel)</Label>
                  <Select
                    value={formData.location_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, location_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecteer locatie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen</SelectItem>
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="cost-submit-btn">
                  Opslaan
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-base">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-5 h-5 text-[#57534E]" />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[140px] justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(filters.startDate, 'dd MMM yyyy', { locale: nl })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={filters.startDate}
                  onSelect={(date) => date && setFilters({ ...filters, startDate: date })}
                  locale={nl}
                />
              </PopoverContent>
            </Popover>
            <span className="text-[#57534E]">tot</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[140px] justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(filters.endDate, 'dd MMM yyyy', { locale: nl })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={filters.endDate}
                  onSelect={(date) => date && setFilters({ ...filters, endDate: date })}
                  locale={nl}
                />
              </PopoverContent>
            </Popover>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleExportPDF}>
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportExcel}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="kpi-tile">
          <p className="kpi-label">Food Cost</p>
          <p className="kpi-value">{formatPercentage(foodCostPercentage)}</p>
          <p className="text-sm text-[#57534E]">{formatCurrency(totalFoodCost)}</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Beverage Cost</p>
          <p className="kpi-value">{formatPercentage(beverageCostPercentage)}</p>
          <p className="text-sm text-[#57534E]">{formatCurrency(totalBeverageCost)}</p>
        </div>
        <div className="kpi-tile border-2 border-[#C2410C]/20 bg-[#C2410C]/5">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart className="w-4 h-4 text-[#C2410C]" />
            <p className="kpi-label text-[#C2410C]">Inkoop Kosten</p>
          </div>
          <p className="kpi-value text-[#C2410C]">{formatPercentage(inkoopCostPercentage)}</p>
          <p className="text-sm text-[#57534E]">{formatCurrency(totalInkoopKosten)}</p>
          <p className="text-xs text-[#78716C] mt-1">{invoices.length} facturen • {purchaseOrders.length} bestellingen</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Personeelskosten</p>
          <p className="kpi-value">{formatPercentage(personnelCostPercentage)}</p>
          <p className="text-sm text-[#57534E]">{formatCurrency(totalPersonnelCost)}</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Bruto Marge</p>
          <p className="kpi-value text-emerald-600">{formatPercentage(bruttoMargePercentage)}</p>
          <p className="text-sm text-[#57534E]">{formatCurrency(bruttoWinst)}</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Netto Resultaat</p>
          <p className={`kpi-value ${nettoResultaat >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {formatCurrency(nettoResultaat)}
          </p>
          <p className="text-sm text-[#57534E]">na alle kosten</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-base">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Kosten per Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="week" tick={{ fontSize: 12, fill: '#57534E' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#57534E' }} tickFormatter={(v) => `€${v}`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="food_cost" name="Food" fill="#C2410C" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="beverage_cost" name="Beverage" fill="#D97706" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="personnel_cost" name="Personeel" fill="#1A2E05" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Omzet vs Kosten</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="week" tick={{ fontSize: 12, fill: '#57534E' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#57534E' }} tickFormatter={(v) => `€${v}`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" name="Omzet" stroke="#15803D" strokeWidth={2} />
                  <Line type="monotone" dataKey="food_cost" name="Food" stroke="#C2410C" strokeWidth={2} />
                  <Line type="monotone" dataKey="personnel_cost" name="Personeel" stroke="#1A2E05" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inkoop Kosten Trend */}
      {inkoopTrend && inkoopTrend.weeks && inkoopTrend.weeks.length > 0 && (
        <Card className="card-base border-2 border-[#C2410C]/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#C2410C]/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#C2410C]" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Inkoop Kosten Trend</CardTitle>
                  <p className="text-sm text-[#57534E]">Laatste 8 weken</p>
                </div>
              </div>
              {inkoopTrend.summary && (
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-sm text-[#57534E]">Gem. per week</p>
                    <p className="text-lg font-bold text-[#1C1917]">{formatCurrency(inkoopTrend.summary.average_weekly_cost)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[#57534E]">Week-over-week</p>
                    <p className={`text-lg font-bold flex items-center gap-1 ${inkoopTrend.summary.week_over_week_change > 0 ? 'text-rose-600' : inkoopTrend.summary.week_over_week_change < 0 ? 'text-emerald-600' : 'text-[#57534E]'}`}>
                      {inkoopTrend.summary.week_over_week_change > 0 ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : inkoopTrend.summary.week_over_week_change < 0 ? (
                        <TrendingDown className="w-4 h-4" />
                      ) : null}
                      {inkoopTrend.summary.week_over_week_change > 0 ? '+' : ''}{inkoopTrend.summary.week_over_week_change}%
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={inkoopTrend.weeks}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="week" tick={{ fontSize: 12, fill: '#57534E' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#57534E' }} tickFormatter={(v) => `€${v}`} />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), name === 'total_cost' ? 'Totaal Inkoop' : name === 'invoice_cost' ? 'Facturen' : 'Bestellingen']}
                    labelFormatter={(label) => `Week ${label}`}
                  />
                  <Legend />
                  <Bar dataKey="invoice_cost" name="Facturen" fill="#C2410C" stackId="inkoop" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="order_cost" name="Bestellingen" fill="#D97706" stackId="inkoop" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Week Details */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
              {inkoopTrend.weeks.map((week, idx) => {
                const isCurrentWeek = idx === inkoopTrend.weeks.length - 1;
                const prevWeek = idx > 0 ? inkoopTrend.weeks[idx - 1] : null;
                const change = prevWeek && prevWeek.total_cost > 0 
                  ? ((week.total_cost - prevWeek.total_cost) / prevWeek.total_cost * 100) 
                  : 0;
                
                return (
                  <div 
                    key={week.week} 
                    className={`p-2 rounded-lg text-center ${isCurrentWeek ? 'bg-[#C2410C]/10 border-2 border-[#C2410C]' : 'bg-stone-50 border border-stone-200'}`}
                  >
                    <p className={`text-xs font-medium ${isCurrentWeek ? 'text-[#C2410C]' : 'text-[#57534E]'}`}>
                      {week.week}
                    </p>
                    <p className={`text-sm font-bold ${isCurrentWeek ? 'text-[#C2410C]' : 'text-[#1C1917]'}`}>
                      {formatCurrency(week.total_cost)}
                    </p>
                    {change !== 0 && (
                      <p className={`text-xs ${change > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                        {change > 0 ? '+' : ''}{change.toFixed(0)}%
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inkoop Kosten Section */}
      {(invoices.length > 0 || purchaseOrders.length > 0) && (
        <Card className="card-base border-2 border-[#C2410C]/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#C2410C]/10 flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#C2410C]" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Inkoop Kosten</CardTitle>
                  <p className="text-sm text-[#57534E]">Facturen en bestellingen uit Voorraad & Inkoop</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-[#C2410C]">{formatCurrency(totalInkoopKosten)}</p>
                <p className="text-sm text-[#57534E]">{formatPercentage(inkoopCostPercentage)} van omzet</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Kosten per Leverancier */}
            {Object.keys(allSupplierCosts).length > 0 && (
              <div>
                <h4 className="font-semibold text-[#1C1917] mb-3">Kosten per Leverancier</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {Object.entries(allSupplierCosts)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 8)
                    .map(([supplier, amount]) => (
                      <div key={supplier} className="p-3 bg-stone-50 rounded-lg border border-stone-200">
                        <p className="font-medium text-[#1C1917] truncate">{supplier}</p>
                        <p className="text-lg font-bold text-[#C2410C]">{formatCurrency(amount)}</p>
                        <p className="text-xs text-[#57534E]">
                          {totalInkoopKosten > 0 ? formatPercentage((amount / totalInkoopKosten) * 100) : '0%'} van inkoop
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Recente Facturen */}
            {invoices.length > 0 && (
              <div>
                <h4 className="font-semibold text-[#1C1917] mb-3">Recente Facturen ({invoices.length})</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Datum</TableHead>
                      <TableHead>Leverancier</TableHead>
                      <TableHead>Factuurnummer</TableHead>
                      <TableHead>Producten</TableHead>
                      <TableHead className="text-right">Bedrag</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices
                      .sort((a, b) => new Date(b.invoice_date || b.created_at) - new Date(a.invoice_date || a.created_at))
                      .slice(0, 10)
                      .map((invoice) => (
                        <TableRow key={invoice.id} className="table-row">
                          <TableCell>{formatDate(invoice.invoice_date || invoice.created_at)}</TableCell>
                          <TableCell className="font-medium">{invoice.supplier_name || '-'}</TableCell>
                          <TableCell>{invoice.invoice_number || '-'}</TableCell>
                          <TableCell>{invoice.items_count || invoice.items?.length || 0} items</TableCell>
                          <TableCell className="text-right font-bold text-[#C2410C]">
                            {formatCurrency(invoice.total_amount || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Recente Bestellingen */}
            {purchaseOrders.length > 0 && (
              <div>
                <h4 className="font-semibold text-[#1C1917] mb-3">Ontvangen Bestellingen ({purchaseOrders.length})</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Datum</TableHead>
                      <TableHead>Leverancier</TableHead>
                      <TableHead>Bestelnummer</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Bedrag</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchaseOrders
                      .sort((a, b) => new Date(b.order_date || b.created_at) - new Date(a.order_date || a.created_at))
                      .slice(0, 10)
                      .map((order) => (
                        <TableRow key={order.id} className="table-row">
                          <TableCell>{formatDate(order.order_date || order.created_at)}</TableCell>
                          <TableCell className="font-medium">{order.supplier_name || '-'}</TableCell>
                          <TableCell>{order.order_number || order.id.slice(0, 8)}</TableCell>
                          <TableCell>
                            <span className="px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700 capitalize">
                              {order.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-bold text-[#C2410C]">
                            {formatCurrency(order.total || order.total_amount || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Event Kosten Section */}
      {eventCosts.length > 0 && (
        <Card className="card-base border-2 border-purple-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <PartyPopper className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Event Kosten</CardTitle>
                  <p className="text-sm text-[#57534E]">Kosten gekoppeld aan events uit Marketing & Acties</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-purple-600">{formatCurrency(totalEventCosts)}</p>
                <p className="text-sm text-[#57534E]">{formatPercentage(eventCostPercentage)} van omzet</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Kosten per Event */}
            {Object.keys(eventCostsByEvent).length > 0 && (
              <div>
                <h4 className="font-semibold text-[#1C1917] mb-3">Kosten per Event</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(eventCostsByEvent)
                    .sort((a, b) => b[1].amount - a[1].amount)
                    .map(([eventName, data]) => (
                      <div key={eventName} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-2 mb-2">
                          <PartyPopper className="w-4 h-4 text-purple-600" />
                          <p className="font-medium text-[#1C1917] truncate">{eventName}</p>
                        </div>
                        <p className="text-xl font-bold text-purple-600">{formatCurrency(data.amount)}</p>
                        <p className="text-xs text-[#57534E]">{data.items.length} kosten item(s)</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Alle Event Kosten */}
            <div>
              <h4 className="font-semibold text-[#1C1917] mb-3">Alle Event Kosten ({eventCosts.length})</h4>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Datum</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Omschrijving</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead className="text-right">Bedrag</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventCosts
                    .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))
                    .map((cost) => (
                      <TableRow key={cost.id} className="table-row">
                        <TableCell>{formatDate(cost.date)}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                            <PartyPopper className="w-3 h-3" />
                            {cost.event_name}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{cost.description}</TableCell>
                        <TableCell className="capitalize">{cost.category}</TableCell>
                        <TableCell className="text-right font-bold text-purple-600">
                          {formatCurrency(cost.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Arrangement Kosten Section */}
      {arrangementCosts.length > 0 && (
        <Card className="card-base border-2 border-amber-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Package className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Arrangement Kosten</CardTitle>
                  <p className="text-sm text-[#57534E]">Kosten gekoppeld aan arrangementen uit Marketing & Acties</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalArrangementCosts)}</p>
                <p className="text-sm text-[#57534E]">{formatPercentage(arrangementCostPercentage)} van omzet</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Kosten per Arrangement */}
            {Object.keys(arrangementCostsByArr).length > 0 && (
              <div>
                <h4 className="font-semibold text-[#1C1917] mb-3">Kosten per Arrangement</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(arrangementCostsByArr)
                    .sort((a, b) => b[1].amount - a[1].amount)
                    .map(([arrName, data]) => (
                      <div key={arrName} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-4 h-4 text-amber-600" />
                          <p className="font-medium text-[#1C1917] truncate">{arrName}</p>
                        </div>
                        <p className="text-xl font-bold text-amber-600">{formatCurrency(data.amount)}</p>
                        <p className="text-xs text-[#57534E]">{data.items.length} kosten item(s)</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Alle Arrangement Kosten */}
            <div>
              <h4 className="font-semibold text-[#1C1917] mb-3">Alle Arrangement Kosten ({arrangementCosts.length})</h4>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Datum</TableHead>
                    <TableHead>Arrangement</TableHead>
                    <TableHead>Omschrijving</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead className="text-right">Bedrag</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {arrangementCosts
                    .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))
                    .map((cost) => (
                      <TableRow key={cost.id} className="table-row">
                        <TableCell>{formatDate(cost.date)}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                            <Package className="w-3 h-3" />
                            {cost.arrangement_name}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{cost.description}</TableCell>
                        <TableCell className="capitalize">{cost.category}</TableCell>
                        <TableCell className="text-right font-bold text-amber-600">
                          {formatCurrency(cost.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fixed Costs Table */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Vaste Kosten</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Omschrijving</TableHead>
                <TableHead>Bedrag</TableHead>
                <TableHead>Frequentie</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fixedCosts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8 text-[#57534E]">
                    Nog geen vaste kosten toegevoegd
                  </TableCell>
                </TableRow>
              ) : (
                fixedCosts.map((cost) => (
                  <TableRow key={cost.id} className="table-row">
                    <TableCell className="font-medium">{cost.name}</TableCell>
                    <TableCell>{formatCurrency(cost.amount)}</TableCell>
                    <TableCell className="capitalize">{cost.frequency}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default KostenMargesPage;
